import { useState } from 'react';

import { Button } from '@/_shared/components/ui/button';
import type { InteractiveAnswer, QuestionBlockPayload } from '@/_shared/types';

import { questionAnswered, questionDismissed, questionSkipped } from './answer-helpers';
import { InteractiveCard } from './InteractiveCard';
import { QuestionSubmitButton } from './QuestionSubmitButton';
import { buildSingleEntry, singleSubmitDisabledReason, validDefaults } from './selection';
import { SingleSelectList } from './SingleSelectList';

interface Props {
  payload: QuestionBlockPayload;
  onResolve: (answer: InteractiveAnswer) => void;
  submitting?: boolean;
}

/**
 * Single-select question block: the prompt as the card title, the option list as the body,
 * Skip + Submit in the footer. Selection UI lives in SingleSelectList (shared with the wizard).
 */
export function SingleSelectQuestion({ payload, onResolve, submitting }: Props): React.ReactElement {
  const question = payload.questions[0]!;
  const [value, setValue] = useState<string | null>(validDefaults(question)[0] ?? null);
  const [otherText, setOtherText] = useState('');

  const entry = buildSingleEntry(question, value, otherText);
  const disabledReason = singleSubmitDisabledReason(value, otherText);

  return (
    <InteractiveCard
      title={question.prompt}
      stats={payload.stats}
      onDismiss={() => onResolve(questionDismissed(payload.blockId))}
      dismissDisabled={submitting}
      busy={submitting}
      ariaLabel={question.prompt}
      footerRight={
        <>
          <Button variant="secondary" disabled={submitting} onClick={() => onResolve(questionSkipped(payload.blockId))}>
            Skip
          </Button>
          <QuestionSubmitButton
            disabledReason={disabledReason}
            loading={submitting}
            onClick={() => entry && onResolve(questionAnswered(payload.blockId, [entry]))}
          >
            Submit
          </QuestionSubmitButton>
        </>
      }
    >
      <SingleSelectList
        question={question}
        value={value}
        onValueChange={setValue}
        otherText={otherText}
        onOtherTextChange={setOtherText}
        disabled={submitting}
      />
    </InteractiveCard>
  );
}
